import {
  filterConditionRows,
  type FilterConditionRow,
} from '@/features/sliced-form/utils/filter-summary'
import { HISTORY_COLUMN_MAP } from '@/features/ticker-stats/schemas/history-groups'

/**
 * A suggested name for a Stock Selection (Chris, 2026-11-01): the name is
 * required, and typing one should not stand between a trader and a run. It
 * comes from the filter preset the conditions were loaded from, else from the
 * condition labels as the face lists them.
 */

/** The longest name suggested; a name rides in run lists and report headers. */
export const SUGGESTED_NAME_MAX_LENGTH = 48

/** Labels joined into one suggestion, at most. */
const MAX_LABELS = 3

/** The name a filter form gives a filter set that was never saved as a preset. */
const UNSAVED_PRESET_NAME = 'Custom'

function capped(text: string): string {
  if (text.length <= SUGGESTED_NAME_MAX_LENGTH) return text
  return `${text.slice(0, SUGGESTED_NAME_MAX_LENGTH - 1).trimEnd()}…`
}

/**
 * The preset's name when there is one; else the first two or three condition
 * labels joined with commas, each added only while the whole stays within the
 * cap. The date window is left out unless it is the only condition. Empty when
 * there is nothing to name from.
 */
export function suggestUniverseName({
  presetName,
  rows,
}: {
  presetName?: string | null
  rows: readonly Pick<FilterConditionRow, 'display' | 'isDate'>[]
}): string {
  const preset = presetName?.trim()
  if (preset && preset !== UNSAVED_PRESET_NAME) return capped(preset)

  const conditions = rows.filter((row) => !row.isDate)
  const labels = (conditions.length > 0 ? conditions : rows)
    .map((row) => row.display.trim())
    .filter(Boolean)
  if (labels.length === 0) return ''

  let name = labels[0]
  for (const label of labels.slice(1, MAX_LABELS)) {
    const next = `${name}, ${label}`
    if (next.length > SUGGESTED_NAME_MAX_LENGTH) break
    name = next
  }
  return capped(name)
}

/** The same, from a filter tree as the node stores it. */
export function suggestUniverseNameFromFilters(
  filters: unknown,
  presetName?: string | null,
): string {
  return suggestUniverseName({
    presetName,
    rows: filters ? filterConditionRows(filters, HISTORY_COLUMN_MAP) : [],
  })
}
